import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { SerializedEditorState } from 'lexical';
import { useAtomValue } from 'jotai';
import { isAfter } from 'date-fns';
import { useMemo } from 'react';
import { UserAvatarList } from '../../../../components/AvatarListTooltip/UserAvatarList';
import { GenericFallbacksWrapper } from '../../../../components/Fallback/GenericFallbacksWrapper';
import { FMT } from '../../../../util/formatter-service';
import { useShowSummaryV2 } from '../useShowSummaryV2';
import { EntityType } from '../../../../data-models/field2.data-model';
import { INoteDataModel } from '../../../../data-models/note.data-model';
import { IUserDataModel } from '../../../../data-models/user.data-model';
import { usersByEmailMapAtom } from '../../../../services/state/AppConfigStateJ';
import {
  AuditorDateRangesPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../../data-models/preferences-v3.data-model';
import { Commentary } from '../../../../schemas/Commentary.schema';
import { PermissionService } from '../../../../services/PermissionService';
import { commentariesByCompanyIdState } from '../../../../services/state/CommentariesState';
import { filteredNotesByCompanyStateJ } from '../../../../services/state/NoteStateJ';
import { preferenceAtom } from '../../../../util/preferences-service-v3';
import { selectedCompanyIdProfileAtom } from '../../../CompanyProfiles/state/UIStateJ';
import {
  CommentarySummaryCard,
  getCommentaryText,
} from '../../../CompanyProfiles/components/ActivityDrawer/Commentaries/CommentarySummaryCard';
import { TertiaryForbiddenMessage } from '../constants/forbidden-messages.constants';
import { Panel } from '../Panel';
import { SummarySection } from '../SummarySection';
import { NoDataMessage2 } from '../../../CompanyProfiles/components/Messages/HelpMessage';
import { getNoteAttendeeIds, getNoteText, NoteSummaryCard } from './NoteSummaryCard';

const auditorDateRangesPreferenceAtom = preferenceAtom<AuditorDateRangesPreferences>({
  preferenceKey: PreferenceKey.auditorDateRanges,
  scope: PreferenceScope.ORGANIZATION,
});

type MostRecentActivity =
  { type: 'note'; item: INoteDataModel<unknown> } | { type: 'commentary'; item: Commentary } | null;

export function RecentActivityPanel() {
  const showSummaryV2 = useShowSummaryV2();

  if (!showSummaryV2) {
    return (
      <GenericFallbacksWrapper forbiddenFallback={TertiaryForbiddenMessage}>
        <RecentActivityPanelV1 />
      </GenericFallbacksWrapper>
    );
  }

  return (
    <SummarySection title={'Last Activity'} forbiddenFallback={TertiaryForbiddenMessage}>
      <RecentActivityPanelContent />
    </SummarySection>
  );
}

/**
 * V1 reads the activity atoms *outside* `Panel`, as `main` does. That placement matters: a 403 from
 * those reads has to reach the outer `GenericFallbacksWrapper`, which is where
 * `TertiaryForbiddenMessage` lives. Reading them inside `Panel` instead would let `Panel`'s own
 * boundary — which has no forbidden config — catch it and show the generic error.
 */
function RecentActivityPanelV1() {
  const mostRecent = useMostRecentActivity();

  return (
    <Panel title={'Last Activity'} style={{ gap: '0.5rem' }}>
      <ActivityBody mostRecent={mostRecent} />
    </Panel>
  );
}

function RecentActivityPanelContent() {
  return <ActivityBody mostRecent={useMostRecentActivity()} />;
}

function useMostRecentActivity(): MostRecentActivity {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const allNotes = useAtomValue(filteredNotesByCompanyStateJ(companyId));
  const allCommentaries = useAtomValue(commentariesByCompanyIdState(companyId));
  const auditorPreferences = useAtomValue(auditorDateRangesPreferenceAtom);

  return useMemo(() => {
    const filteredCommentaries = filterCommentariesForAuditor(allCommentaries ?? [], auditorPreferences);
    return getMostRecentActivity(allNotes ?? [], filteredCommentaries);
  }, [allNotes, allCommentaries, auditorPreferences]);
}

function ActivityBody({ mostRecent }: { mostRecent: MostRecentActivity }) {
  const showSummaryV2 = useShowSummaryV2();

  if (!mostRecent) {
    return <NoDataMessage2 message='No recent activity available' />;
  }

  if (showSummaryV2) {
    return <ActivityRowV2 mostRecent={mostRecent} />;
  }

  if (mostRecent.type === 'note') {
    return <NoteSummaryCard note={mostRecent.item as INoteDataModel<SerializedEditorState>} />;
  }
  return <CommentarySummaryCard commentary={mostRecent.item} />;
}

const v2Header = css`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
`;

const v2Meta = css`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 0.5rem;
`;

/** The design sets the entry's text on its own panel, inset from the card behind it. */
const v2TextPanel = css`
  background: var(--mui-palette-background-paper);
  border-radius: 0.25rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
`;

/** Two lines then an ellipsis, as the design truncates it. */
const v2Text = css`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
`;

/**
 * The design's activity row: the entry's own label on the left, its timestamp and author pushed
 * right, and the text plain underneath — no inset panel. Built here rather than inside
 * `NoteSummaryCard` / `CommentarySummaryCard`, which the Activity Drawer also renders.
 */
function ActivityRowV2({ mostRecent }: { mostRecent: NonNullable<MostRecentActivity> }) {
  const isNote = mostRecent.type === 'note';
  const note = isNote ? (mostRecent.item as INoteDataModel<SerializedEditorState>) : undefined;
  const commentary = isNote ? undefined : (mostRecent.item as Commentary);

  const label = note ? note.title : getCommentaryLabel(commentary!);
  const timestamp = note ? note.updatedAt : commentary?.createdAt;
  const text = note ? getNoteText(note) : getCommentaryText(commentary!);

  // The design shows an avatar after the timestamp for either kind of entry. A note carries its
  // attendees as ids; a commentary only records its author's email, so resolve that to a user.
  const usersByEmail = useAtomValue(usersByEmailMapAtom);
  const author = commentary?.createdBy ? usersByEmail.get(commentary.createdBy) : undefined;
  const users: number[] | IUserDataModel[] = note ? getNoteAttendeeIds(note) : author ? [author] : [];

  return (
    <div>
      <div css={v2Header}>
        <Typography variant='body2'>{label}</Typography>
        <div css={v2Meta}>
          {timestamp && (
            <Typography variant='caption' color='text.secondary' whiteSpace='nowrap'>
              {`${FMT.format('date', timestamp)} at ${FMT.format('time12hr', timestamp)}`}
            </Typography>
          )}
          <UserAvatarList users={users} />
        </div>
      </div>
      <div css={v2TextPanel}>
        <Typography variant='body2' css={v2Text}>
          {text}
        </Typography>
      </div>
    </div>
  );
}

/**
 * A commentary's own name, which is what the design labels the row with. Derived from the entity the
 * commentary hangs off, matching the wording `CommentaryEditor` shows over the same content — not
 * `commentary.type`, which is not this label.
 */
function getCommentaryLabel(commentary: Commentary): string {
  return commentary.entity === EntityType.companyInvestmentThesis
    ? 'Investment Thesis'
    : 'Performance Commentary';
}

function getCommentaryDate(commentary: Commentary): string | null {
  if (commentary.entity === EntityType.companyInvestmentThesis) {
    return commentary.createdAt ?? null;
  }
  return commentary.date ?? null;
}

export function filterCommentariesForAuditor(
  commentaries: Commentary[],
  preferences: AuditorDateRangesPreferences | null | undefined
): Commentary[] {
  const isAuditor = PermissionService.get().isAuditor();
  if (!isAuditor || !preferences?.restrictToRange) {
    return commentaries;
  }

  const startDate = new Date(preferences.startDate);
  const endDate = new Date(preferences.endDate);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return commentaries;
  }

  return commentaries.filter((commentary) => {
    const dateStr = getCommentaryDate(commentary);
    if (!dateStr) {
      return false;
    }
    const commentaryDate = new Date(dateStr);
    return commentaryDate >= startDate && commentaryDate <= endDate;
  });
}

export function getLastNote(notes: INoteDataModel<unknown>[]) {
  return notes.reduce(
    (latest, note) => {
      if (!latest) {
        return note;
      }
      return isAfter(new Date(note.updatedAt), new Date(latest.updatedAt)) ? note : latest;
    },
    null as INoteDataModel<unknown> | null
  );
}

export function parseValidDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

export function getLastCommentary(commentaries: Commentary[]) {
  return commentaries.reduce(
    (latest, commentary) => {
      if (!parseValidDate(commentary.date)) {
        return latest;
      }
      if (!latest) {
        return commentary;
      }
      const latestDate = parseValidDate(latest.date);
      const currentDate = parseValidDate(commentary.date)!;
      if (!latestDate) {
        return commentary;
      }
      return isAfter(currentDate, latestDate) ? commentary : latest;
    },
    null as Commentary | null
  );
}

export function getMostRecentActivity(
  notes: INoteDataModel<unknown>[],
  commentaries: Commentary[]
): MostRecentActivity {
  const lastNote = getLastNote(notes);
  const lastCommentary = getLastCommentary(commentaries);

  if (!lastNote && !lastCommentary) {
    return null;
  }
  if (!lastNote) {
    return { type: 'commentary', item: lastCommentary! };
  }
  if (!lastCommentary) {
    return { type: 'note', item: lastNote };
  }

  const noteDate = new Date(lastNote.updatedAt);
  const commentaryDate = parseValidDate(lastCommentary.date);
  if (!commentaryDate) {
    return { type: 'note', item: lastNote };
  }

  return isAfter(noteDate, commentaryDate)
    ? { type: 'note', item: lastNote }
    : { type: 'commentary', item: lastCommentary };
}
